import { pinProblems, type GeoPoint } from './coordinates';
import { foldStreetText } from './mailingAddress';

/**
 * A Google Maps link read for the place it names (piece 10; decision 1600). A
 * customer drops a pin in Google Maps and sends the link, and the application
 * reads the point from it: *"a site location for like a meeting or creating a
 * location via link"* (product owner, 2026-10-02).
 *
 * 🔨 Pure: it reads the text and asks nothing. A short link carries no point
 * until the API expands it with one request, so the reading says it is short
 * and the server does the rest. The text-message pickup of the phone work reads
 * a link through the same function.
 */

/** What one reading of pasted text answers. */
export type MapsLinkReading =
  /** A Maps link carrying a point: the coordinates, and the link as read out of the text. */
  | { kind: 'point'; point: GeoPoint; link: string }
  /** A short Maps link, which names its place only once expanded. */
  | { kind: 'short'; link: string }
  /** A Maps link that carries no point: a search by name, a route, a map with no place. */
  | { kind: 'noPoint'; link: string }
  /** Not a Google Maps link at all. */
  | { kind: 'notMaps' };

/** The short-link hosts Google hands out from Share; each answers one redirect to the long link. */
const SHORT_HOSTS = ['maps.app.goo.gl'];

/** A latitude and longitude written as one value, `40.4864091,-79.1933976`, with an optional `loc:` prefix and spaces. */
const PAIR = /^\s*(?:loc:\s*)?(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/i;

/** The query parameters a long link names its place by, in the order they are trusted. */
const POINT_PARAMETERS = ['q', 'query', 'll', 'destination', 'daddr', 'center', 'sll'];

/** The first web address in the text, so a link pasted with a sentence around it still reads. */
function firstUrl(text: string): URL | null {
  const trimmed = text.trim();
  const found = /https?:\/\/[^\s<>"']+/i.exec(trimmed)?.[0] ?? (/^(?:maps\.app\.goo\.gl|goo\.gl|(?:www\.|maps\.)?google\.[a-z.]+)\//i.test(trimmed) ? `https://${trimmed.split(/\s/)[0]}` : null);
  if (!found) return null;
  try {
    return new URL(found.replace(/[).,;]+$/, ''));
  } catch {
    return null;
  }
}

/** A Google host: google.com and its country domains, with or without `www.` or `maps.` in front. */
function isGoogleHost(host: string): boolean {
  return /^(?:www\.|maps\.)?google\.(?:[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/i.test(host);
}

/** A decode that answers the text as it stands where a stray percent sign would throw. */
function decoded(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function pointOf(latitude: string, longitude: string): GeoPoint | null {
  const point = { latitude: Number(latitude), longitude: Number(longitude) };
  return pinProblems(point.latitude, point.longitude).length === 0 ? point : null;
}

function pairOf(value: string | null | undefined): GeoPoint | null {
  const match = value ? PAIR.exec(value) : null;
  return match ? pointOf(match[1]!, match[2]!) : null;
}

/**
 * The point a long Maps link carries, by the form it is written in:
 * 🔨 the place's own `!3d<lat>!4d<lng>` first, because `@lat,lng` beside it is
 * where the map was centred, not where the pin stands; then a point named as
 * the query (`?q=`, `?query=`, `?ll=` and their kin); then a point written as
 * the place's path; then the map's centre, on a place link or a link with no
 * path alone.
 */
export function pointInMapsUrl(url: URL): GeoPoint | null {
  const whole = decoded(url.href);
  const data = /!3d(-?\d{1,2}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/.exec(whole);
  if (data) {
    const point = pointOf(data[1]!, data[2]!);
    if (point) return point;
  }
  for (const name of POINT_PARAMETERS) {
    const point = pairOf(url.searchParams.get(name));
    if (point) return point;
  }
  // 🔨 A route names two places and neither is the pin, so a directions path reads no point from its segments or its centre.
  if (url.pathname.startsWith('/maps/dir')) return null;
  const segments = url.pathname.split('/').map((part) => decoded(part.replace(/\+/g, ' ')));
  for (const segment of segments) {
    const point = pairOf(segment);
    if (point) return point;
  }
  // 🔨 The map's centre stands for a place only on a place link or a link with no path: a route's or a search's centre is where the map was looking, not a pin.
  const path = url.pathname.replace(/\/+$/, '');
  if (path !== '' && path !== '/maps' && !/^\/maps\/@[^/]*$/.test(path) && !path.startsWith('/maps/place/')) return null;
  const centre = /@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/.exec(whole);
  return centre ? pointOf(centre[1]!, centre[2]!) : null;
}

/**
 * Reads pasted text as a Google Maps link (decision 1600): the short
 * `maps.app.goo.gl/…` and `goo.gl/maps/…`, and the long `maps.google.com/?q=`,
 * `google.com/maps/place/…/@…`, `google.com/maps/search/?api=1&query=` and the
 * `!3d…!4d…` data form.
 */
export function readMapsLink(text: string | null | undefined): MapsLinkReading {
  const url = firstUrl(text ?? '');
  if (!url) return { kind: 'notMaps' };
  const host = url.hostname.toLowerCase();
  const link = url.href;
  if (SHORT_HOSTS.includes(host) || (host === 'goo.gl' && url.pathname.startsWith('/maps'))) return { kind: 'short', link };
  const isMaps = (host.startsWith('maps.') && isGoogleHost(host)) || (isGoogleHost(host) && url.pathname.startsWith('/maps'));
  if (!isMaps) return { kind: 'notMaps' };
  const point = pointInMapsUrl(url);
  return point ? { kind: 'point', point, link } : { kind: 'noPoint', link };
}

/** Where a part of the answered address came from: the map service's own key, or the Census Bureau's (decision 0983's names). */
export type MapsLinkSource = 'openstreetmap' | 'google' | 'census' | 'geoapify';

/** The address a link's point was answered with, in a Location's own parts. */
export interface MapsLinkAddress {
  countryCode: string;
  addressLines: string[];
  locality: string | null;
  municipality: string | null;
  administrativeArea: string | null;
  county: string | null;
  postalCode: string | null;
}

/** What the reading could not answer, named so the form says it rather than filling a guess (decision 1600). */
export type MapsLinkUnread = 'expansion' | 'point' | 'address' | 'stateAndCounty';

/** How the read went: a place read, a link that is not Maps, or a Maps link with no point in it. */
export type MapsLinkOutcome = 'place' | 'notMaps' | 'noPoint';

/**
 * The API's answer for one pasted link (decision 1600): the point, the
 * address with each part's source, and the link as pasted. 🔨 Nothing is
 * written on the person's behalf; the form fills from this for them to read.
 */
export interface MapsLinkAnswer {
  /** The text as pasted. */
  link: string;
  /** The long link a short one expanded to, where it was short. */
  expandedLink: string | null;
  outcome: MapsLinkOutcome;
  point: GeoPoint | null;
  address: MapsLinkAddress | null;
  /** Which service answered each part that was answered. */
  sources: Partial<Record<keyof MapsLinkAddress, MapsLinkSource>>;
  unread: MapsLinkUnread[];
}

/** The sentence each unread part reads as beneath the field. */
export const MAPS_LINK_UNREAD_KEYS = {
  expansion: 'mapsLink.unread.expansion',
  point: 'mapsLink.unread.point',
  address: 'mapsLink.unread.address',
  stateAndCounty: 'mapsLink.unread.stateAndCounty',
} as const satisfies Record<MapsLinkUnread, string>;

/** How each source is named beside what it answered (decision 0983's labels). */
export const MAPS_LINK_SOURCE_KEYS = {
  openstreetmap: 'mapsLink.source.openstreetmap',
  google: 'mapsLink.source.google',
  census: 'mapsLink.source.census',
  geoapify: 'mapsLink.source.geoapify',
} as const satisfies Record<MapsLinkSource, string>;

/** The pin a link's point is written as on save: the point, and the link as pasted beside it. */
export interface MapsLinkPin {
  latitude: number;
  longitude: number;
  link: string;
}

/** The pin an answer gives, or none where it read no point. */
export function mapsLinkPin(answer: MapsLinkAnswer | null | undefined): MapsLinkPin | null {
  if (!answer?.point) return null;
  return { latitude: answer.point.latitude, longitude: answer.point.longitude, link: answer.link };
}

/**
 * The street line an event's Place field searches its places by, where the
 * answer carries one with a house number (decision 1600). 🔨 A road alone
 * names no building, so a link that answered only a road searches nothing and
 * Add Site opens instead of a guess at which place on the road was meant.
 */
export function linkStreetLine(answer: MapsLinkAnswer | null | undefined): string | null {
  const line = answer?.address?.addressLines[0]?.trim() ?? '';
  return /^\d/.test(line) ? line : null;
}

/** The first offered place whose name or address carries the link's street line, folded; none where no place stands there. */
export function placeAtStreetLine<T extends { label: string; detail?: string | null }>(places: readonly T[], line: string): T | null {
  const wanted = foldStreetText(line);
  if (!wanted) return null;
  // Whole words only: 14 Springer Lane is not a place at 114 Springer Lane.
  const pattern = new RegExp(`(^|[^a-z0-9])${wanted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`);
  return places.find((place) => pattern.test(foldStreetText(`${place.label} ${place.detail ?? ''}`))) ?? null;
}
